import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Badge } from '@cuspar/ui';
import { Check, Globe, Shield, Zap, Server, HandCoinsIcon, ArrowRight, Settings, ArrowUp, ArrowDown, X, Pencil } from 'lucide-react';
import { Link } from 'react-router';
import { InfoTooltip } from '../../components/UI/InfoTooltip';
import { useCartStore } from '../../state/cartStore';
import { BillingInterval, ICommonAddItemToCartRequest, IPrice, ProductCategory, StaticPlanTier, SubscriptionProduct } from '@cuspar/ywt.ch.model';
import { LoadingIndicator } from '../../components/UI/LoadingIndicator';
import { PriceGadget } from '../../components/UI/PriceGadget';
import { useEffect, useState } from 'react';
import { authEntryRoute, useAuthStore } from '../../state/authStore';
import { ROUTES } from '../../routes';
import { useNavigate } from 'react-router';
import { PlanChangeDialog } from '../../components/Subscription/PlanChangeDialog';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { YWT_PUBLIC_CONFIG } from '@cuspar/ywt.ch.config/public';

const Feature = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => {
  return (
    <div className='flex flex-row md:flex-col items-center text-left md:text-center p-4'>
      <div className='bg-primary/10 p-3 rounded-full mr-4 md:mr-0 md:mb-4 text-primary shrink-0'>{icon}</div>
      <div>
        <h3 className='font-semibold mb-1 md:mb-2'>{title}</h3>
        <p className='text-xs text-muted-foreground'>{description}</p>
      </div>
    </div>
  );
};

export const StaticHostingPlanPage = () => {
  const { t } = useTranslation();

  if (!YWT_PUBLIC_CONFIG.staticHosting.salesClosed) {
    return <StaticHostingPlansOpen />;
  }

  return (
    <div className='min-h-[60vh] flex items-center justify-center px-4'>
      <div className='max-w-2xl rounded-2xl border bg-card p-8 md:p-12 text-center shadow-sm'>
        <div className='mx-auto mb-6 flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground'>
          <Server className='size-7' />
        </div>
        <h1 className='font-mono text-2xl md:text-4xl font-bold leading-tight tracking-tighter mb-4'>{t('hosting.salesClosedTitle')}</h1>
        <p className='text-sm md:text-base text-muted-foreground'>{t('hosting.salesClosedDescription')}</p>
      </div>
    </div>
  );
};

const StaticHostingPlansOpen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prices = useCartStore((state) => state.prices);
  const staticHostingPrices = prices.filter((p) => p.product === ProductCategory.staticHosting);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(BillingInterval.yearly);
  const starterPrice = staticHostingPrices.find((p) => p.plan === StaticPlanTier.Starter && (p.interval === billingInterval || (!p.interval && billingInterval === BillingInterval.yearly)));
  const businessPrice = staticHostingPrices.find((p) => p.plan === StaticPlanTier.Business && (p.interval === billingInterval || (!p.interval && billingInterval === BillingInterval.yearly)));
  const starterMonthlyPrice = staticHostingPrices.find((p) => p.plan === StaticPlanTier.Starter && p.interval === BillingInterval.monthly);
  const starterYearlyPrice = staticHostingPrices.find((p) => p.plan === StaticPlanTier.Starter && (p.interval === BillingInterval.yearly || !p.interval));
  const isMonthly = billingInterval === BillingInterval.monthly;
  const activeSubscriptions = useAuthStore((state) => state.activeSubscriptions);
  const refreshActiveSubscriptions = useAuthStore((state) => state.getActiveSubscriptions);
  const existingStaticHostingSubscription = activeSubscriptions.find((s) => s.product === SubscriptionProduct.staticSite);
  const addItemToCart = useCartStore((state) => state.addItemToCart);
  const cart = useCartStore((state) => state.cart);
  const user = useAuthStore((state) => state.user);
  // Fetch active subscriptions when a signed-in user views this page
  useEffect(() => {
    if (user && activeSubscriptions.length === 0) {
      refreshActiveSubscriptions();
    }
  }, [user]);

  const [addingToCartPlan, setAddingToCartPlan] = useState<StaticPlanTier | null>(null);
  const [planChangeDialogOpen, setPlanChangeDialogOpen] = useState(false);
  const [planChangeTarget, setPlanChangeTarget] = useState<StaticPlanTier | null>(null);

  async function addPlanToCart(plan: StaticPlanTier) {
    setAddingToCartPlan(plan);
    const planPrice = getStaticHostingPrice(plan, prices, billingInterval);
    const staticHostingCartItem: ICommonAddItemToCartRequest = {
      type: 'staticHosting',
      item: {
        plan: plan,
        price: planPrice?.price ?? 0,
        interval: billingInterval,
      },
    };
    await addItemToCart(staticHostingCartItem);
    setAddingToCartPlan(null);
  }

  function isPlanInCart(plan: StaticPlanTier) {
    if (!cart) return false;
    return cart.staticHosting?.plan === plan;
  }

  const hasSubscription = !!existingStaticHostingSubscription;
  const currentTier = existingStaticHostingSubscription?.tier;

  function openPlanChangeDialog(tier: StaticPlanTier) {
    setPlanChangeTarget(tier);
    setPlanChangeDialogOpen(true);
  }

  function handlePlanChangeSuccess() {
    setPlanChangeDialogOpen(false);
    setPlanChangeTarget(null);
    // Refresh subscriptions to update the UI
    refreshActiveSubscriptions();
  }

  return (
    <div className='min-h-screen bg-background font-mono text-foreground'>
      <h1 className='font-mono text-center text-2xl md:text-4xl font-bold leading-tight tracking-tighter lg:leading-[1.1] mb-2'>{t('hosting.plansTitle')}</h1>
      <h2 className='text-center font-mono text-sm text-muted-foreground mb-10'>{t('hosting.plansSubtitle')}</h2>

      {/* Existing subscriber banner */}
      {hasSubscription && (
        <section className='container mx-auto px-4 mb-8'>
          <div className='max-w-4xl mx-auto bg-muted/50 border border-border rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4'>
            <div className='flex items-center gap-3'>
              <Badge
                variant='secondary'
                className={`px-3 py-1 ${
                  currentTier === StaticPlanTier.Business
                    ? 'bg-linear-to-r from-slate-200 to-zinc-300 dark:from-slate-600 dark:to-zinc-500 text-slate-800 dark:text-slate-100 border border-slate-300 dark:border-slate-500'
                    : 'bg-primary/10 text-primary border border-primary/20'
                }`}
              >
                {t('hosting.tierPlan', { tier: currentTier })}
              </Badge>
              <span className='text-sm text-muted-foreground'>{t('hosting.alreadySubscribed')}</span>
            </div>
            <Button onClick={() => navigate(ROUTES.account.websites)} size='sm'>
              <Settings className='w-4 h-4 mr-2' />
              {t('hosting.manageYourPlan')}
              <ArrowRight className='w-4 h-4 ml-2' />
            </Button>
          </div>
        </section>
      )}

      <section className='bg-muted/50 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto rounded-xl shadow-sm'>
        <Feature icon={<Shield className='w-6 h-6' />} title={t('hosting.featureHttpsTitle')} description={t('hosting.featureHttpsDesc')} />
        <Feature icon={<Globe className='w-6 h-6' />} title={t('hosting.featureFastTitle')} description={t('hosting.featureFastDesc')} />
        <Feature icon={<Zap className='w-6 h-6' />} title={t('hosting.featureDomainsTitle')} description={t('hosting.featureDomainsDesc')} />
        <Feature icon={<Server className='w-6 h-6' />} title={t('hosting.featureSwissTitle')} description={t('hosting.featureSwissDesc')} />
      </section>

      {/* 3. Pricing Section — needs price data; show a loader until it arrives.
          The rest of the page (hero, features, FAQ) renders without it so it
          prerenders for SEO. */}
      {!starterPrice || !businessPrice ? (
        <div className='container mx-auto px-4 py-24 flex justify-center'>
          <LoadingIndicator />
        </div>
      ) : (
        <section className='container mx-auto px-4 py-16 md:py-24' id='plans'>
          {/* Billing Interval Toggle */}
          <div className='flex justify-center mb-10'>
            <div className='inline-flex items-center gap-1 bg-muted rounded-lg p-1'>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${isMonthly ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setBillingInterval(BillingInterval.monthly)}
              >
                {t('hosting.billingMonthly')}
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${!isMonthly ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => setBillingInterval(BillingInterval.yearly)}
              >
                {t('hosting.billingYearly')}
                {starterMonthlyPrice && starterYearlyPrice && (
                  <Badge variant='secondary' className='ml-2 text-xs bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20'>
                    {t('hosting.savePercent', { percent: Math.round((1 - starterYearlyPrice.price / (starterMonthlyPrice.price * 12)) * 100) })}
                  </Badge>
                )}
              </button>
            </div>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-start'>
            {/* Plan 1: Free */}
            <div className='border border-border rounded-xl p-6 bg-card shadow-sm'>
              <h3 className='text-lg font-bold mb-2'>{t('common.free')}</h3>
              <div className='text-3xl font-bold mb-1'>
                <PriceGadget price={0} currency={starterPrice.currency} />
              </div>
              <p className='text-sm text-muted-foreground mb-6'>{t('hosting.noSubscriptionRequired')}</p>
              <p className='text-sm mb-6 min-h-[40px]'>{t('hosting.freeTrialDesc')}</p>
              <Link to={user ? ROUTES.account.websites : authEntryRoute()} state={user ? undefined : { from: ROUTES.account.websites }}>
                <Button variant='outline' className='w-full mb-6' size='lg'>
                  <Pencil className='w-4 h-4 mr-2' />
                  {t('hosting.startForFree')}
                </Button>
              </Link>
              <ul className='space-y-2 text-sm'>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-slate-500 dark:text-slate-400' /> {t('hosting.oneWebsite')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-slate-500 dark:text-slate-400' /> {t('hosting.embeddedEditor')}
                </li>

                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-slate-500 dark:text-slate-400' /> {t('hosting.fileZipUpload')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-slate-500 dark:text-slate-400' /> {t('hosting.previewLinkFeature')}
                </li>
                <li className='flex gap-2 items-center text-muted-foreground'>
                  <X className='w-4 h-4 text-muted-foreground/50' /> {t('hosting.publishNeedsPlanFeature')}
                </li>
                <li className='flex gap-2 items-center text-muted-foreground'>
                  <X className='w-4 h-4 text-muted-foreground/50' /> {t('hosting.noCustomDomainFeature')}
                </li>
              </ul>
              <p className='text-xs text-center text-muted-foreground mt-6 pt-4 border-t'>{t('hosting.publishOnStarter')}</p>
            </div>

            {/* Plan 2: Starter */}
            <div className='border-2 border-primary rounded-xl p-6 bg-card shadow-lg relative'>
              <div className='absolute top-0 right-0 left-0 -mt-4 flex justify-center'>
                <span className='bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide'>{t('hosting.popularLabel')}</span>
              </div>
              <h3 className='text-xl font-bold mb-2'>Starter</h3>
              <div className='text-3xl font-bold mb-1'>
                <PriceGadget price={isMonthly ? starterPrice.price : starterPrice.price / 12} currency={starterPrice.currency} />{' '}
                <span className='text-lg font-normal text-muted-foreground'>{t('common.perMonth')}</span>
              </div>
              <p className='text-sm text-muted-foreground mb-6'>
                {isMonthly ? (
                  t('hosting.billedMonthly')
                ) : (
                  <>
                    {t('hosting.billedYearlyFor')} <PriceGadget price={starterPrice.price} currency={starterPrice.currency} /> {t('common.perYear')}
                  </>
                )}
              </p>
              <p className='text-sm mb-6 min-h-[40px]'>{t('hosting.starterDesc')}</p>
              {currentTier === StaticPlanTier.Starter ? (
                <Button variant='outline' className='w-full mb-6 bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-400 cursor-default' size='lg'>
                  <Check className='w-4 h-4 mr-2' /> {t('hosting.yourCurrentPlan')}
                </Button>
              ) : currentTier === StaticPlanTier.Business ? (
                <Button variant='outline' className='w-full mb-6' size='lg' onClick={() => openPlanChangeDialog(StaticPlanTier.Starter)}>
                  <ArrowDown className='w-4 h-4 mr-2' /> {t('hosting.downgradeToStarter')}
                </Button>
              ) : (
                <Button
                  disabled={isPlanInCart(StaticPlanTier.Starter)}
                  className='w-full mb-6'
                  size='lg'
                  loading={addingToCartPlan === StaticPlanTier.Starter}
                  onClick={() => addPlanToCart(StaticPlanTier.Starter)}
                >
                  {isPlanInCart(StaticPlanTier.Starter) ? t('common.inCart') : t('hosting.getStarter')}
                </Button>
              )}
              <ul className='space-y-3 text-sm'>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-green-500' /> {t('hosting.featuresFromFree')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-green-500' /> {t('hosting.oneWebsite')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-green-500' /> {t('hosting.oneCustomDomain')} <InfoTooltip info={t('hosting.customDomainTooltip')} />
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-green-500' /> {t('hosting.fastSecureDeliveryFeature')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-green-500' /> {t('hosting.websiteAnalysis')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-green-500' /> {t('hosting.emailSupportFeature')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-green-500' /> {t('hosting.fourteenDayGuarantee')}
                </li>
              </ul>
              <p className='text-xs text-center font-medium text-primary mt-6 pt-4 border-t border-primary/20'>{t('hosting.perfectForPersonal')}</p>
            </div>

            {/* Plan 3: Business - Premium titanium aesthetic */}
            <div className='border border-slate-300 dark:border-slate-600 rounded-xl p-6 bg-linear-to-br from-slate-50 via-zinc-50 to-slate-100 dark:from-card dark:via-card/80 dark:to-card shadow-sm hover:shadow-md transition-all relative overflow-hidden'>
              {/* Subtle metallic sheen overlay */}
              <div className='absolute inset-0 bg-linear-to-tr from-transparent via-white/5 to-transparent pointer-events-none' />
              <h3 className='text-xl font-bold mb-2 relative'>Business</h3>
              <div className='text-3xl font-bold mb-1'>
                <PriceGadget price={isMonthly ? businessPrice.price : businessPrice.price / 12} currency={businessPrice.currency} />{' '}
                <span className='text-lg font-normal text-muted-foreground'>{t('common.perMonth')}</span>
              </div>
              <p className='text-sm text-muted-foreground mb-6'>
                {isMonthly ? (
                  t('hosting.billedMonthly')
                ) : (
                  <>
                    {t('hosting.billedYearlyFor')} <PriceGadget price={businessPrice.price} currency={businessPrice.currency} /> {t('common.perYear')}
                  </>
                )}
              </p>
              <p className='text-sm mb-6 min-h-[40px]'>{t('hosting.businessDesc')}</p>
              {currentTier === StaticPlanTier.Business ? (
                <Button variant='outline' className='w-full mb-6 bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-400 cursor-default relative' size='lg'>
                  <Check className='w-4 h-4 mr-2' /> {t('hosting.yourCurrentPlan')}
                </Button>
              ) : currentTier === StaticPlanTier.Starter ? (
                <Button className='w-full mb-6' size='lg' onClick={() => openPlanChangeDialog(StaticPlanTier.Business)}>
                  <ArrowUp className='w-4 h-4 mr-2' /> {t('hosting.upgradeToBusinessButton')}
                </Button>
              ) : (
                <Button
                  disabled={isPlanInCart(StaticPlanTier.Business)}
                  variant='outline'
                  className='w-full mb-6'
                  size='lg'
                  loading={addingToCartPlan === StaticPlanTier.Business}
                  onClick={() => addPlanToCart(StaticPlanTier.Business)}
                >
                  {isPlanInCart(StaticPlanTier.Business) ? t('common.inCart') : t('hosting.chooseBusiness')}
                </Button>
              )}
              <ul className='space-y-3 text-sm relative'>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-slate-500 dark:text-slate-400' /> {t('hosting.featuresFromStarter')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-slate-500 dark:text-slate-400' /> {t('hosting.upToFiveWebsites')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-slate-500 dark:text-slate-400' /> {t('hosting.upToTenDomains')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-slate-500 dark:text-slate-400' /> {t('hosting.higherTraffic')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-slate-500 dark:text-slate-400' /> {t('hosting.prioritySupport')}
                </li>
                <li className='flex gap-2 items-center'>
                  <Check className='w-4 h-4 text-slate-500 dark:text-slate-400' /> {t('hosting.thirtyDayGuarantee')}
                </li>
              </ul>
              <p className='text-xs text-center font-medium text-slate-600 dark:text-slate-400 mt-6 pt-4 border-t border-slate-300 dark:border-slate-600 relative'>{t('hosting.needMoreWebsites')}</p>
            </div>
          </div>
        </section>
      )}

      {/* 4. Domain Bundle Callout */}
      <section className='container mx-auto px-4 mb-8 md:mb-24'>
        <div className='max-w-4xl mx-auto bg-primary/5 rounded-2xl p-8 md:p-12 border border-primary/10 flex flex-col md:flex-row items-center justify-between gap-8'>
          <div className='flex-1'>
            <h2 className='text-xl md:text-2xl font-bold mb-4'>{t('hosting.moneyBackTitle')}</h2>
            <p className='text-sm md:text-base text-muted-foreground'>{t('hosting.moneyBackDesc')}</p>
          </div>
          <HandCoinsIcon className='w-10 h-10 text-success mb-2' />
        </div>
      </section>

      {/* 5. What's Included */}
      <section className='container mx-auto px-4 mb-16 max-w-4xl'>
        <h3 className='text-xl md:text-2xl font-bold text-center mb-8'>{t('hosting.whatsIncludedAll')}</h3>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-y-4 gap-x-8'>
          {[t('hosting.httpsIncludedFeature'), t('hosting.managedDns'), t('hosting.noServerMaintenance'), t('hosting.fairUseTraffic'), t('hosting.noHiddenFeesFeature')].map((label) => (
            <div key={label} className='flex gap-3 items-center'>
              <div className='bg-green-100 dark:bg-green-900/30 p-1 rounded-full'>
                <Check className='w-4 h-4 text-green-600' />
              </div>
              <span className='font-medium text-sm md:text-base'>{label}</span>
            </div>
          ))}
        </div>
        <p className='text-xs text-center text-muted-foreground mt-8'>{t('hosting.fairUseNote')}</p>
      </section>

      {/* FAQ Section */}
      <section className='container max-w-4xl mx-auto pb-12 px-4'>
        <h2 className='text-lg font-medium mb-4 text-center text-muted-foreground'>{t('common.faq')}</h2>
        <Accordion type='single' collapsible className='w-full grid md:grid-cols-2 gap-x-8'>
          <AccordionItem value='what-is-static'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqWhatIsStaticQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqWhatIsStaticA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='dynamic-content'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqDynamicQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqDynamicA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='custom-domain'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqCustomDomainQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqCustomDomainA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='deploy-methods'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqDeployQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqDeployA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='https-ssl'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqHttpsQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqHttpsA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='vat'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqVatQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqVatA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='refund'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqRefundQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqRefundA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='free-plan'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqFreePlanQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqFreePlanA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='upgrade'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqUpgradeQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqUpgradeA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='fair-usage'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqFairUsageQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqFairUsageA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='data-location'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqDataLocationQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqDataLocationA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='analytics'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqAnalyticsQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('hosting.faqAnalyticsA')}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value='support'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('hosting.faqSupportQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              <Trans
                i18nKey='hosting.faqSupportA'
                components={{
                  email: (
                    <Link to='mailto:support@ywt.ch' className='underline'>
                      support@ywt.ch
                    </Link>
                  ),
                }}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      {/* Plan Change Dialog */}
      {existingStaticHostingSubscription && planChangeTarget && (
        <PlanChangeDialog
          open={planChangeDialogOpen}
          onOpenChange={setPlanChangeDialogOpen}
          subscription={existingStaticHostingSubscription}
          newTier={planChangeTarget}
          onSuccess={handlePlanChangeSuccess}
        />
      )}
    </div>
  );
};

export function getStaticHostingPrice(plan: StaticPlanTier, prices: IPrice[], interval?: BillingInterval) {
  return prices.find((p) => p.product === ProductCategory.staticHosting && p.plan === plan && (interval ? p.interval === interval || (!p.interval && interval === BillingInterval.yearly) : true));
}
